<template>
  <div>
    <div
      class="layout-wrapper layout-static layout-menu-light layout-topbar-blue"
    >
      <div class="layout-topbar">
        <img
          :src="$root.images.beLogoTransparentBackground"
          alt="logo"
          class="layout-topbar-logo"
        />
      </div>
    </div>
    <main :class="{ 'has-appointment-form': showAppointmentForm }" class="my-8">
      <div class="flex justify-content-center align-items-center w-full">
        <InlineMessage
          v-if="!isTokenValid && messageError"
          severity="error"
          class="mt-3 max-width-card"
        >
          {{ t(messageError) }}
        </InlineMessage>
        <Skeleton
          v-else-if="isLoading"
          width="100%"
          height="400px"
          class="max-width-card"
        ></Skeleton>
        <div
          v-else
          class="card shadow-3 max-width-card"
          :class="{ 'appointment-form-active': showAppointmentForm }"
        >
          <div v-if="showAppointmentForm" class="back-button-container mb-3">
            <Button
              icon="pi pi-arrow-left"
              class="back-button p-button-text p-button-lg"
              aria-label="Voltar"
              @click="goBackToOptions"
            />
          </div>

          <section class="opportunity-header mb-5 pb-4">
            <Skeleton
              v-if="isLoading"
              width="100%"
              height="40px"
              class="mb-2"
            ></Skeleton>
            <h1 v-else class="service-title">
              {{ opportunityData && opportunityData.servicePriceName }}
            </h1>

            <Skeleton v-if="isLoading" width="80%" height="20px"></Skeleton>
            <p v-else class="client-name">
              {{ opportunityData && opportunityData.clientName }}
            </p>
          </section>

          <section
            v-show="!messageSuccess && !showAppointmentForm"
            class="options-section"
          >
            <p class="section-label">Como deseja proceder?</p>

            <div class="options-grid">
              <div class="option-card">
                <Button
                  :label="`Ligar para o BE (${
                    opportunityData && opportunityData.phone
                  })`"
                  icon="pi pi-phone"
                  class="option-button call-button w-full"
                  :loading="isLoadingUpdate"
                  @click.prevent=""
                />
              </div>

              <div class="option-card">
                <Button
                  label="Marcar um agendamento"
                  icon="pi pi-calendar"
                  class="option-button schedule-button w-full"
                  :loading="isLoadingUpdate"
                  :disabled="!isServiceAvailable()"
                  @click.prevent="selectAppointment"
                />
              </div>

              <div class="option-card">
                <Button
                  label="Pedido de contacto"
                  icon="pi pi-envelope"
                  class="option-button contact-button w-full"
                  :loading="isLoadingUpdate"
                  @click.prevent="selectContact"
                />
              </div>

              <div class="option-card">
                <Button
                  label="Não tenho interesse"
                  icon="pi pi-times"
                  class="option-button reject-button w-full"
                  :loading="isLoadingUpdate"
                  @click.prevent="selectForfeit"
                />
              </div>
            </div>

            <InlineMessage v-if="messageError" severity="error" class="mt-4">
              {{ t(messageError) }}
            </InlineMessage>

            <ProgressBar
              v-if="isLoadingUpdate"
              mode="indeterminate"
              class="mt-4"
            />
          </section>

          <section v-if="messageSuccess" class="success-section">
            <InlineMessage severity="success" class="mt-3">
              {{ t(messageSuccess) }}
            </InlineMessage>
          </section>

          <section v-show="showAppointmentForm">
            <AppointmentWizard
              :service-price-id="
                opportunityData ? opportunityData.servicePriceId : null
              "
              :country="opportunityData ? opportunityData.country : null"
              :client-name="opportunityData ? opportunityData.clientName : null"
              :activation-token="token"
              @services-loaded="onServicesLoaded"
              @success="handleAppointmentSuccess"
            />
          </section>
        </div>
      </div>
    </main>
    <ConfirmDialog />
  </div>
</template>

<script>
import Button from "primevue/button";
import InlineMessage from "primevue/inlinemessage";
import Skeleton from "primevue/skeleton";
import ProgressBar from "primevue/progressbar";
import ConfirmDialog from "primevue/confirmdialog";
import { defineAsyncComponent } from "vue";

const AppointmentWizard = defineAsyncComponent(() =>
  import("@/components/appointments/AppointmentWizard.vue")
);

export default {
  components: {
    Button,
    InlineMessage,
    Skeleton,
    ProgressBar,
    ConfirmDialog,
    AppointmentWizard,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isTokenValid: false,
      messageError: "",
      messageSuccess: "",
      showAppointmentForm: false,
      isLoading: false,
      isLoadingUpdate: false,
      opportunityData: null,
      availableServices: [],
    };
  },
  mounted() {
    this.tokenConfirmation();
  },
  methods: {
    tokenConfirmation() {
      this.messageError = "";
      this.messageSuccess = "";
      this.isLoading = true;

      this.$root
        .apiGet(this.$root.getApiActivate(this.token))
        .then((response) => {
          this.opportunityData = response.data.data;
          this.isTokenValid = true;
          this.isLoading = false;
        })
        .catch((error) => {
          this.isTokenValid = false;
          this.isLoading = false;
          this.messageError =
            (error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
            "O pedido expirou";
        });
    },

    onServicesLoaded(services) {
      this.availableServices = services;
    },

    isServiceAvailable() {
      if (!this.opportunityData || !this.opportunityData.servicePriceId) {
        return false;
      }
      return this.availableServices.some(
        (service) =>
          Number(service.id) === Number(this.opportunityData.servicePriceId)
      );
    },

    selectAppointment() {
      this.messageError = this.messageSuccess = "";
      this.showAppointmentForm = true;
    },

    selectContact() {
      this.$confirm.require({
        message: "Quer que entremos em contacto consigo?",
        header: "Pedido de Contacto",
        icon: "pi pi-info-circle",
        rejectLabel: "Cancelar",
        acceptLabel: "Confirmar",
        accept: () => this.submitContact(),
        reject: () => {
          this.messageError = "";
        },
      });
    },

    submitContact() {
      this.messageError = this.messageSuccess = "";
      this.isLoadingUpdate = true;
      this.$root.axiosSetAuth(null, false);
      this.$root
        .apiPut(this.$root.getApiActivateContact(this.token), {})
        .then(() => {
          this.messageSuccess = "Pedido de contacto registado com sucesso";
          this.isLoadingUpdate = false;
        })
        .catch((error) => {
          this.isLoadingUpdate = false;
          if (
            error.response &&
            error.response.data &&
            error.response.data.msg_code
          ) {
            this.messageError = error.response.data.msg_code;
          } else {
            this.messageError = "Erro ao registar pedido de contacto";
          }
        });
    },

    selectForfeit() {
      this.$confirm.require({
        message: "Tem a certeza que não tem interesse no serviço?",
        header: "Confirmar Rejeição",
        icon: "pi pi-exclamation-triangle",
        rejectLabel: "Cancelar",
        acceptLabel: "Confirmar",
        accept: () => this.submitForfeit(),
        reject: () => {
          this.messageError = "";
        },
      });
    },

    submitForfeit() {
      this.messageError = this.messageSuccess = "";
      this.isLoadingUpdate = true;
      this.$root.axiosSetAuth(null, false);
      this.$root
        .apiPut(this.$root.getApiActivateForfeit(this.token), {})
        .then(() => {
          this.messageSuccess =
            "Fomos informados que não tem interesse no serviço";
          this.isLoadingUpdate = false;
        })
        .catch((error) => {
          this.isLoadingUpdate = false;
          if (
            error.response &&
            error.response.data &&
            error.response.data.msg_code
          ) {
            this.messageError = error.response.data.msg_code;
          } else {
            this.messageError =
              "Erro ao registar que não tem interesse no serviço";
          }
        });
    },

    goBackToOptions() {
      this.showAppointmentForm = false;
      this.messageError = "";
      this.messageSuccess = "";
    },

    handleAppointmentSuccess() {
      this.showAppointmentForm = false;
    },

    t(key) {
      return this.$t(key);
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.layout-topbar {
  display: list-item;
  top: 0;
}

main {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

main.has-appointment-form {
  align-items: flex-start;
  padding-top: 2rem;
}

.min-height-screen {
  min-height: 80vh;
}

.max-width-card {
  width: 100%;
  max-width: 500px;
}

.max-width-card.appointment-form-active {
  max-width: 70vw;
}

.card {
  padding: 2rem;
  border-radius: 8px;
}

.opportunity-header {
  border-bottom: 2px solid #f0f1f3;
  padding-bottom: 1.5rem;
  text-align: center;
}

.service-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
  word-break: break-word;
}

.client-name {
  font-size: 1rem;
  color: #555;
  margin: 0;
  font-weight: 500;
}

.options-section {
  width: 100%;
}

.section-label {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
}

@media (min-width: 600px) {
  .options-grid {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
}

.option-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option-button {
  padding: 0.75rem 1rem !important;
  font-weight: 600;
  border: none !important;
  border-radius: 6px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 44px;
  font-size: 0.95rem;
  text-transform: none;
}

.option-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.option-button:active {
  transform: translateY(0);
}

.call-button {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
  color: white !important;
}

.call-button:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
}

.schedule-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
}

.schedule-button:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
}

.contact-button {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
}

.contact-button:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%) !important;
}

.reject-button {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%) !important;
  color: white !important;
}

.reject-button:hover {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important;
}

.success-section {
  text-align: center;
  padding: 2rem 0;
}

.success-icon {
  font-size: 3rem;
  color: #10b981;
  margin-bottom: 1rem;
}

.success-icon i {
  display: block;
}

:deep(.p-inline-message) {
  width: 100%;
  border-radius: 6px;
  border: none;
  font-size: 0.95rem;
}

:deep(.p-inline-message-icon) {
  margin-right: 0.75rem;
}

:deep(.p-progressbar) {
  height: 4px;
  border-radius: 4px;
}

:deep(.p-skeleton) {
  border-radius: 6px;
}

.option-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .card {
    padding: 1.5rem;
  }

  .service-title {
    font-size: 1.5rem;
  }

  .section-label {
    font-size: 0.95rem;
  }

  .option-button {
    min-height: 48px;
    font-size: 0.9rem;
  }

  .option-subtitle {
    font-size: 0.75rem;
  }

  .success-icon {
    font-size: 2.5rem;
  }
}

.option-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Confirm Dialog Styles */
:deep(.p-confirm-dialog .p-dialog-footer .p-button) {
  padding: 0.75rem 1.5rem !important;
  font-weight: 600 !important;
  border: none !important;
  border-radius: 6px !important;
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%) !important;
}

:deep(.p-confirm-dialog .p-dialog-footer .p-button-label),
:deep(.p-confirm-dialog .p-dialog-footer .p-button span) {
  color: white !important;
  font-weight: 600 !important;
}

:deep(.p-confirm-dialog .p-dialog-footer .p-button:hover) {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important;
}

:deep(.p-confirm-dialog .p-dialog-footer .p-button.p-confirm-dialog-accept) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
}

:deep(.p-confirm-dialog
    .p-dialog-footer
    .p-button.p-confirm-dialog-accept:hover) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
}

/* Allow scrolling on body for appointment form */
:global(body) {
  overflow: auto !important;
}
</style>
